import { useEffect, useState } from 'react';
import { getCurrentUser } from '@aws-amplify/auth';
import {
	Box,
	Button,
	CircularProgress,
	TextField,
	Typography,
} from '@mui/material';
import ssoConfig from './amplify-sso-config';

export default function SSOLogin() {
	const [email, setEmail] = useState('');
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		async function checkAuth() {
			try {
				await getCurrentUser();
				window.location.href = '/';
			} catch {
				setLoading(false);
			}
		}
		checkAuth();
	}, []);

	const handleSubmit = (e) => {
		e.preventDefault();
		setError('');

		const domain = email.split('@')[1]?.toLowerCase();
		if (!domain) {
			setError('Please enter a valid email address.');
			return;
		}

		// Build the Cognito authorize URL directly rather than using signInWithRedirect.
		// Amplify v6 passes `identity_provider` for named providers but `idp_identifier`
		// is the correct parameter for Cognito to resolve a provider via its IdpIdentifiers list.
		// Constructing the URL manually ensures Cognito skips the hosted UI and routes
		// straight to the matched SAML IdP.
		const params = new URLSearchParams({
			response_type: 'code',
			client_id: ssoConfig.aws_user_pools_web_client_id,
			redirect_uri: `${window.location.origin}/sso/callback`,
			scope: 'openid email',
			idp_identifier: domain,
		});

		window.location.href = `https://${ssoConfig.oauth.domain}/oauth2/authorize?${params.toString()}`;
	};

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box
			display="flex"
			flexDirection="column"
			alignItems="center"
			justifyContent="center"
			minHeight="100vh"
			sx={{ backgroundColor: '#e0e0e0' }}
		>
			<Box
				display="flex"
				flexDirection="column"
				alignItems="center"
				gap={3}
				sx={{
					padding: 4,
					backgroundColor: '#ffffff',
					borderRadius: 2,
					boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
					width: '400px',
					maxWidth: '90%',
				}}
			>
				<img
					alt="Velocity logo"
					src="https://images.squarespace-cdn.com/content/v1/6321facc7e681a5a6cd5d9b9/6c844dce-2a1e-4852-a864-9486a500ff90/Horizontal+Velocity++updated.png?format=1000w"
					style={{ maxWidth: '250px' }}
				/>
				<Typography variant="h5" sx={{ color: '#333333' }}>Single Sign-On</Typography>
				<Typography variant="body2" sx={{ color: '#666666' }}>
					Enter your work email to sign in with your organization.
				</Typography>
				<Box
					component="form"
					onSubmit={handleSubmit}
					display="flex"
					flexDirection="column"
					gap={2}
					width="100%"
				>
					<TextField
						label="Work email"
						type="email"
						value={email}
						onChange={(e) => { setEmail(e.target.value); setError(''); }}
						required
						autoFocus
						fullWidth
						sx={{
							'& .MuiOutlinedInput-root': {
								backgroundColor: '#f9f9f9',
							},
							'& .MuiInputLabel-root': {
								color: '#555555',
							},
							'& .MuiOutlinedInput-input': {
								color: '#000000',
							},
						}}
					/>
					{error && (
						<Typography color="error" variant="body2">
							{error}
						</Typography>
					)}
					<Button type="submit" variant="contained" size="large" fullWidth>
						Continue
					</Button>
				</Box>
			</Box>
		</Box>
	);
}
